import { DatePickerInput } from '@creditornot/cb-date-picker';
import { InputContainer } from '@creditornot/cb-form';
import { DateTime } from 'luxon';
import React, { useContext, useEffect, useState } from 'react';
import { FieldInputProps } from 'react-final-form';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import TimePicker from 'common/components/weekly-schedules/TimePairInput/TimePicker';

import { asLocaleNaiveDate } from './utils';
import { MenuLocaleContext } from '../../../MenuLocaleContext';

type Props = FieldInputProps<number | undefined, HTMLElement> & {
  dateHint?: string | undefined;
  disabledDays?: { before: Date };
};

const Root = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledInputContainer = styled(InputContainer)`
  & + & {
    padding-block-start: 8px;
  }
`;

const StyledTimePicker = styled(TimePicker)`
  width: 64px;
`;

const buildDateInMillis = (dateValue: Date, timeValue: number, timezone: string) => {
  const hour = Math.floor(timeValue / 60);
  const minute = timeValue % 60;
  const month = dateValue.getMonth() + 1;
  const day = dateValue.getDate();
  const year = dateValue.getFullYear();
  return DateTime.fromJSDate(dateValue, { zone: timezone }).set({ hour, minute, day, month, year }).toMillis();
};

const AvailabilityInputs = ({
  onChange,
  value,
  disabled = false,
  disabledDays = undefined,
  dateHint = undefined,
}: Props) => {
  const { timezone } = useContext(MenuLocaleContext);
  const intl = useIntl();

  const [dateValue, setDateValue] = useState<Date | undefined>(undefined);
  const [timeValue, setTimeValue] = useState<number>(0);

  useEffect(() => {
    const valueAsDateTime = value ? DateTime.fromMillis(value, { zone: timezone }) : null;
    const updatedDateValue = value ? asLocaleNaiveDate(value, timezone) : undefined;
    const updatedTimeValue = valueAsDateTime ? valueAsDateTime.hour * 60 + valueAsDateTime.minute : 0;

    setDateValue(updatedDateValue);
    // If we have some legitimate date value, then change the current time/minutes value as well
    // Otherwise clearing the date input will reset the time/minutes as well
    if (updatedDateValue) {
      setTimeValue(updatedTimeValue);
    }
  }, [timezone, value]);

  const handleDateChange = (val: Date | undefined) => {
    if (val) {
      onChange(buildDateInMillis(val, timeValue, timezone));
    } else {
      onChange(undefined);
    }
    setDateValue(val);
  };

  const handleTimeChange = (val: number) => {
    if (dateValue) {
      onChange(buildDateInMillis(dateValue, val, timezone));
    }
    setTimeValue(val);
  };

  return (
    <Root>
      <StyledInputContainer label={intl.formatMessage({ id: 'date-time.date' })}>
        <DatePickerInput
          disabled={disabled}
          closeOnChange
          disabledDays={disabledDays}
          onChange={handleDateChange}
          placeholder={intl.formatMessage({ id: 'date-time.date-picker-placeholder' })}
          popoverZIndex={100005}
          value={dateValue}
        />
      </StyledInputContainer>

      <StyledInputContainer label={intl.formatMessage({ id: 'date-time.time' })}>
        <InputContainer hint={dateHint}>
          <StyledTimePicker disabled={disabled} value={timeValue} onChange={handleTimeChange} />
        </InputContainer>
      </StyledInputContainer>
    </Root>
  );
};

export default AvailabilityInputs;
